import * as dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import * as timezone_ from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import { isNil } from 'lodash';
import { Temporal } from 'temporal-polyfill';

/**
 * This is the timezone that we use when no other time zone is specified, and we
 * cannot otherwise infer a time zone from the company or browser. It's the
 * time zone of the Pallet office.
 */
export const PRESUMPTIVE_TIME_ZONE = 'America/Los_Angeles';

dayjs.extend(utc);
dayjs.extend(timezone_);

/** Returns a Dayjs instance rounded up to the nearest half hour. */
export const roundUpToNearestHalfHour = (date: Date | Dayjs): Dayjs => {
  const d = dayjs(date);
  return d.add(30 - (d.minute() % 30), 'minute').startOf('minute');
};

/**
 * This is useful to return a stable date that doesn't change the day
 * regardless of time zone (PST, EST, UTC at least)
 *
 * @param date
 * @returns 12 PM noon on the given day
 */
export const getNoonOfDay = (
  date: Date | Dayjs,
  timezone = PRESUMPTIVE_TIME_ZONE,
): Date => {
  return dayjs(date)
    .set('hour', 12)
    .tz(timezone)
    .set('hour', 12)
    .set('minute', 0)
    .set('second', 0)
    .toDate();
};

/**
 * Returns a Date object set to noon (12:00:00 PM) in the specified timezone
 * for the current day of the input date. This differs from getNoonOfDay
 * in that it wont roll over the returned date to the next day in cases where the
 * utc timestamp and local timestamp are on different days.
 *
 * @param date - The input date to be converted
 * @param timezone - The target timezone (default: 'America/Los_Angeles')
 * @returns A Date object representing noon of the input date in the specified timezone
 */
export const getNoonInTimezone = (
  date: Date | Dayjs,
  timezone = PRESUMPTIVE_TIME_ZONE,
): Date => {
  return dayjs(date)
    .tz(timezone)
    .set('hour', 12)
    .set('minute', 0)
    .set('second', 0)
    .toDate();
};

/**
 * Canonicalizes a supplied instant into the pair of values we persist on
 * `Stop` for service-date bucketing:
 *
 * - `serviceDate`: a `Date` pinned to noon-of-day in the carrier company's
 *   time zone, so daily-bucketed reports get a stable, DST-safe timestamp.
 * - `serviceDateIso8601`: the same plain date as an ISO 8601 string, used
 *   by schedule views and daily lists for grouping.
 *
 * Both must be derived in the company's time zone — deriving in the host
 * process TZ mis-buckets near-midnight orders for any company not on that
 * TZ. This helper exists so the CREATE path (`shipments.service.ts`) and
 * the PATCH path (`api-orders.service.ts`) cannot drift apart.
 *
 * @param instant The supplied instant (e.g. partner-supplied `serviceDate`).
 * @param timeZone IANA time-zone name (e.g. `America/Los_Angeles`).
 * @returns The pair of values to persist on `Stop`.
 */
export const canonicalizeServiceDateInTimezone = (
  instant: Date,
  timeZone: string,
): { serviceDate: Date; serviceDateIso8601: string } => {
  const plainDate = Temporal.Instant.from(instant.toISOString())
    .toZonedDateTimeISO(timeZone)
    .toPlainDate();
  return {
    serviceDate: new Date(
      plainDate
        .toPlainDateTime({ hour: 12 })
        .toZonedDateTime(timeZone).epochMilliseconds,
    ),
    serviceDateIso8601: plainDate.toString(),
  };
};

export const displayConvertedDateTime = ({
  date,
  timeZone = PRESUMPTIVE_TIME_ZONE,
  format = 'MM/DD/YYYY',
}: {
  date: Date | Dayjs | null;
  timeZone?: string;
  format?: string;
}): string => {
  return dayjs(date).tz(timeZone).format(format);
};

/**
 * Formats as M/D/YYYY (no zero-padding).
 *
 * Omitting `timeZone` resolves the calendar date in the runtime's local zone,
 * which is what the frontend call sites have always done. Server-side callers
 * must pass the company's zone: a container runs UTC, so an invoice dated
 * `2024-03-05T02:00:00Z` would otherwise render `3/5/2024` from the server and
 * `3/4/2024` in a US-Pacific browser for the same invoice.
 */
export const transformDateToDateString = (
  date: string,
  timeZone?: string,
): string => {
  if (timeZone !== undefined) {
    return dayjs(date).tz(timeZone).format('M/D/YYYY');
  }
  const dateObject = new Date(date);
  const year = dateObject.getFullYear();
  const month = dateObject.getMonth() + 1;
  const day = dateObject.getDate();
  return `${month}/${day}/${year}`;
};

export const getTodayTimeISOString = (timeDate: Date | string) => {
  return dayjs()
    .utc()
    .set('hour', new Date(timeDate).getUTCHours())
    .set('minute', new Date(timeDate).getUTCMinutes())
    .toISOString();
};

const formatAppointmentDate = (date: Date, timeZone: string): string =>
  dayjs(date).tz(timeZone).format('MM/DD/YY');

const formatAppointmentTime = (time: Date, timeZone: string): string =>
  dayjs(time).tz(timeZone).format('h:mm a');

/**
 * Returns a string with a date and time, or null if date, startTime, and endTime are null.
 * The format is designed for (non-technical) users in that it displays times in a 12-hour format (e.g. 1:30 pm).
 * This function returns as much information as it has, for example returning just the start and end times if
 * they are set but no date is set.
 *
 * At least for now the function name has the word "Appointment" because this isn't meant to be the universal
 * way for formatting date-times.
 */
export const formatAppointmentDateTime = ({
  date,
  startTime,
  endTime,
  timeZone,
}: {
  date: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  timeZone: string;
}): string | null => {
  const formattedDate = isNil(date)
    ? null
    : formatAppointmentDate(date, timeZone);
  const formattedStartTime = isNil(startTime)
    ? null
    : formatAppointmentTime(startTime, timeZone);
  const formattedEndTime = isNil(endTime)
    ? null
    : formatAppointmentTime(endTime, timeZone);

  if (
    !isNil(formattedDate) &&
    !isNil(formattedStartTime) &&
    !isNil(formattedEndTime)
  ) {
    return `${formattedDate}, ${formattedStartTime} - ${formattedEndTime}`;
  }
  if (!isNil(formattedDate) && !isNil(formattedStartTime)) {
    return `${formattedDate}, ${formattedStartTime}`;
  }
  if (!isNil(formattedDate) && !isNil(formattedEndTime)) {
    return `${formattedDate}, ${formattedEndTime}`;
  }
  if (!isNil(formattedDate)) {
    return formattedDate;
  }
  if (!isNil(formattedStartTime) && !isNil(formattedEndTime)) {
    return `${formattedStartTime} - ${formattedEndTime}`;
  }
  return formattedStartTime ?? formattedEndTime;
};

/**
 * Calculates the total time difference in minutes between two dates, rounded down to the nearest minute.
 * Returns null if input is invalid.
 */
export const calculateTimeDifferenceInMinutes = ({
  startDate,
  endDate,
}: {
  startDate: Date | null;
  endDate: Date | null;
}): number | null => {
  if (isNil(startDate) || isNil(endDate)) return null;

  const start = dayjs(startDate).startOf('minute');
  const end = dayjs(endDate).startOf('minute');

  if (end.isBefore(start)) return null;

  return end.diff(start, 'minutes');
};

/**
 * Converts a total number of minutes into hours and minutes.
 * Returns null values if input is null.
 */
export const convertMinutesToHoursAndMinutes = ({
  totalMinutes,
}: {
  totalMinutes: number | null;
}) => {
  if (isNil(totalMinutes)) {
    return { hours: null, minutes: null };
  }
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
};
